import React, { useEffect, useState } from 'react'
import isEqual from 'lodash/isEqual'
import moment from 'moment-timezone'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { useGlobalToast, ToastVariation } from 'transfix-ui/components/Toast'
import { Box, Typography } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { IUpdateRfpStrategyInput } from 'types/graphqlTypes'
import { Strategy } from 'apps/marketplace/components/rfp/types/strategyManager'
import { Tabs, Tab } from 'transfix-ui'
import { FORMATS } from 'constants/datetime'
import { StrategyManagementTab } from './StrategyManagementTab'
import { StrategyNotesTab } from './StrategyNotesTab'
import { StrategyReferenceTab } from './StrategyReferenceTab'
import { useUpdateGlobalRfpStrategiesMutation } from './graphql/UpdateGlobalRfpStrategies'
import { useUpdateLocalRfpStrategiesMutation } from './graphql/UpdateLocalRfpStrategies'

const useStyles = makeStyles({
  modalHeader: {
    // Need !important flag to override
    // !important flag on padding utility
    // class within ModalBase:
    padding: '0 !important',
  },
  modalBody: {
    // Need !important flag to override
    // !important flag on padding utility
    // class within ModalBase:
    padding: '0 !important',
  },
})

enum TABS {
  management = 'Management',
  reference = 'Reference',
  notes = 'Notes',
}

interface IStrategyManagerModalProps extends Omit<IModalBaseProps, 'modalType'> {
  onClose: () => void
  refetchLanes?: (...args: any) => void
  rfpId?: string | null
  rfpTitle?: string | null
}

export function StrategyManagerModal({
  onClose,
  refetchLanes,
  rfpId,
  rfpTitle,
}: IStrategyManagerModalProps) {
  const { openToast } = useGlobalToast()
  const classes = useStyles()
  const [currentTab, setCurrentTab] = useState<TABS>(TABS.management)
  const [strategies, setStrategies] = useState<Strategy[]>([])
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true)
  const [strategyStateIsValid, setStrategyStateIsValid] = useState<boolean>(true)
  const [refetchStrategies, setRefetchStrategies] = useState<boolean>(false)
  const [updatedStrategies, setUpdatedStrategies] = useState<Strategy[]>([])
  const changeCurrentTab = (newTab: TABS) => setCurrentTab(newTab)
  const [currentStrategyId, setCurrentStrategyId] = useState<string | null>(null)
  const [isGlobalDefault, setIsGlobalDefault] = useState<boolean | null>(null)
  const [lastEdited, setLastEdited] = useState<string | null>(null)

  const [updateGlobalRfpStrategies] = useUpdateGlobalRfpStrategiesMutation({})
  const [updateLocalRfpStrategies] = useUpdateLocalRfpStrategiesMutation({})

  const updateStrategies = async () => {
    let message
    let messageType: ToastVariation = 'default'
    const updates: IUpdateRfpStrategyInput[] = []

    setIsSaveDisabled(true)

    try {
      updatedStrategies.forEach(updatedStrategy => {
        strategies?.forEach(strategy => {
          if (updatedStrategy.id === strategy.id) {
            if (!isEqual(updatedStrategy.strategyMatrix, strategy.strategyMatrix)) {
              if (updatedStrategy.name) {
                updates.push({
                  name: updatedStrategy.name,
                  strategyMatrix: updatedStrategy.strategyMatrix,
                })
              }
            }
          }
        })
      })

      if (rfpId) {
        const updateLocalResult = await updateLocalRfpStrategies({
          variables: {
            input: {
              rfpId,
              strategies: updates,
            },
          },
        })

        message =
          updateLocalResult?.errors?.join('. ') ||
          updateLocalResult?.data?.updateLocalRfpStrategies?.errors?.map(e => e.message).join('. ')
      } else {
        const updateResult = await updateGlobalRfpStrategies({
          variables: {
            input: {
              strategies: updates,
            },
          },
        })

        message =
          updateResult?.errors?.join('. ') ||
          updateResult?.data?.updateGlobalRfpStrategies?.errors?.map(e => e.message).join('. ')
      }
    } catch (error) {
      message = { ...error }
    }

    if (message) {
      messageType = 'alert'
      setIsSaveDisabled(false)
    } else {
      message = 'Successfully Updated!'
      setRefetchStrategies(true)

      if (refetchLanes) {
        refetchLanes({})
      }
    }

    openToast({
      toastMessage: message,
      toastType: messageType,
      toastDuration: 6000,
    })

    onClose()
  }

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: onClose,
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: updateStrategies,
      type: 'confirm',
      disabled: isSaveDisabled || !strategyStateIsValid,
      dataTest: 'save-button',
    },
  ]

  // CS: Whenever the strategy in view changes,
  // we have to update the last edited date in the description
  useEffect(() => {
    if (strategies) {
      strategies.forEach(strategy => {
        if (strategy.id === currentStrategyId) {
          setLastEdited(moment(strategy.updatedAt).format(FORMATS.singleDateFormat))

          if (strategy.requestForProposalId && strategy.createdAt === strategy.updatedAt) {
            setIsGlobalDefault(true)
          } else {
            setIsGlobalDefault(false)
          }
        }
      })
    }
  }, [currentStrategyId])

  return (
    <ModalBase
      dataTest='RequestForProposalLaneNotesModal'
      onClose={onClose}
      modalType={MODAL_TYPES.confirmation}
      size={8}
      open
      closeOnBackdropClick
      headerClassName={classes.modalHeader}
      bodyClassName={classes.modalBody}
      actions={actions}>
      <Box p={3}>
        <Typography variant='h3' color='textPrimary' display='block'>
          RFP Pricing Strategy
        </Typography>
        {rfpTitle ? (
          <Typography className='mt1' variant='subtitle2' color='textSecondary'>
            {`${rfpTitle} · ${
              isGlobalDefault ? 'Showing global default set on' : 'Last edited'
            } ${lastEdited}`}
          </Typography>
        ) : (
          <Typography className='mt1' variant='subtitle2' color='textSecondary'>
            Changes made here will affect all future RFPs
            {lastEdited ? ` · Last edited ${lastEdited}` : ''}
          </Typography>
        )}
      </Box>
      <Box>
        <Tabs value={currentTab} onChange={changeCurrentTab}>
          <Tab label={TABS.management}>
            <StrategyManagementTab
              rfpId={rfpId}
              refetchStrategies={refetchStrategies}
              setCurrentStrategyId={setCurrentStrategyId}
              updatedStrategies={updatedStrategies}
              strategyStateIsValid={strategyStateIsValid}
              setIsSaveDisabled={setIsSaveDisabled}
              setRefetchStrategies={setRefetchStrategies}
              setStrategies={setStrategies}
              setStrategyStateIsValid={setStrategyStateIsValid}
              setUpdatedStrategies={setUpdatedStrategies}
            />
          </Tab>
          <Tab label={TABS.reference}>
            <StrategyReferenceTab />
          </Tab>
          <Tab label={TABS.notes}>
            <StrategyNotesTab strategyId={currentStrategyId} />
          </Tab>
        </Tabs>
      </Box>
    </ModalBase>
  )
}
